import React, { useState, useEffect } from "react";
import {
  Modal,
  Spin,
  message,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Table,
  Space,
} from "antd";
import {
  DownloadOutlined,
  PrinterOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  getInvoiceById,
  exportInvoicePDF,
  getCreditById,
  exportCreditPDF,
} from "../../../api/accounting";
import { convertFromUSD } from "../../../utils/currencyUtils";
import dayjs from "dayjs";

const { Title, Text } = Typography;

/**
 * InvoiceViewModal - Display invoice/bill/credit in a formatted template
 * This is a read-only view with export PDF option
 * Supports both invoices/bills and credit notes
 */
const InvoiceViewModal = ({
  visible,
  onCancel,
  invoiceId,
  invoiceType,
  isCredit = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  useEffect(() => {
    if (visible && invoiceId) {
      fetchInvoiceData();
    } else if (!visible) {
      // Reset invoice data when modal closes
      setInvoiceData(null);
    }
  }, [visible, invoiceId, isCredit]);

  const fetchInvoiceData = async () => {
    if (!invoiceId) {
      console.error("No invoice ID provided to fetchInvoiceData");
      message.error("No invoice ID available");
      return;
    }

    setLoading(true);
    try {
      let data;
      if (isCredit) {
        // Fetch credit note data
        data = await getCreditById(invoiceId, true); // include_allocations = true
      } else {
        // Fetch invoice/bill data
        data = await getInvoiceById(invoiceId);
      }

      // Convert amounts from USD back to display currency if not USD
      const currency = data?.currency || "$";
      const isUSD = currency === "$" || currency === "USD";

      if (!isUSD && data) {
        // Load custom rates from localStorage
        let customRates = {};
        try {
          const savedRates = localStorage.getItem("custom_currency_rates");
          if (savedRates) {
            customRates = JSON.parse(savedRates);
          }
        } catch (e) {
          console.error("Failed to load custom rates", e);
        }

        // Convert invoice-level amounts from USD to display currency
        data = {
          ...data,
          subtotal: convertFromUSD(data.subtotal, currency, customRates),
          discount_amount: convertFromUSD(
            data.discount_amount,
            currency,
            customRates
          ),
          total_amount: convertFromUSD(
            data.total_amount,
            currency,
            customRates
          ),
          paid_amount: convertFromUSD(data.paid_amount, currency, customRates),
          // Convert item-level amounts
          items: (data.items || []).map((item) => ({
            ...item,
            unit_price: convertFromUSD(item.unit_price, currency, customRates),
            subtotal: convertFromUSD(item.subtotal, currency, customRates),
            tax_amount: convertFromUSD(item.tax_amount, currency, customRates),
            discount_amount: convertFromUSD(
              item.discount_amount,
              currency,
              customRates
            ),
          })),
          // Convert payment terms amounts
          payment_terms: (data.payment_terms || []).map((term) => ({
            ...term,
            amount: convertFromUSD(term.amount, currency, customRates),
          })),
        };
      }

      setInvoiceData(data);
    } catch (error) {
      console.error(
        `Error fetching ${
          isCredit ? "credit note" : "invoice"
        } with ID ${invoiceId}:`,
        error
      );
      console.error("Error response:", error.response?.data);
      message.error(
        `Failed to load ${isCredit ? "credit note" : "invoice"} details: ${
          error.response?.data?.error || error.message
        }`
      );
      setInvoiceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const response = isCredit
        ? await exportCreditPDF(invoiceId)
        : await exportInvoicePDF(invoiceId);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${isCredit ? "Credit" : invoiceType === "bill" ? "Bill" : "Invoice"}_${
          invoiceData?.number || invoiceId
        }.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      message.error("Failed to export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  const handlePrint = async () => {
    // Print button should export PDF, not print the modal
    await handleExportPDF();
  };

  if (!invoiceData) {
    return (
      <Modal
        title={`View ${invoiceType === "bill" ? "Bill" : "Invoice"}`}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={900}
        centered
        zIndex={2100}
      >
        <Spin spinning={loading} tip="Loading invoice details...">
          <div style={{ minHeight: 200 }} />
        </Spin>
      </Modal>
    );
  }

  // Items table columns
  const itemColumns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Product/Description",
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || record.description}</div>
          {record.product_variation && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.product_variation}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      align: "center",
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 120,
      align: "right",
      render: (value) =>
        `${invoiceData?.currency || "$"} ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      width: 120,
      align: "right",
      render: (value) =>
        `${invoiceData?.currency || "$"} ${parseFloat(value || 0).toFixed(2)}`,
    },
  ];

  // Payment terms table columns
  const paymentTermColumns = [
    {
      title: "Term",
      dataIndex: "term_label",
      key: "term_label",
      render: (text, record) => text || `${record.due_days} days`,
    },
    {
      title: "Due Days",
      dataIndex: "due_days",
      key: "due_days",
      width: 100,
      align: "center",
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 120,
      render: (text, record) => {
        // If due_date is explicitly provided, use it
        if (text) return dayjs(text).format("MMM DD, YYYY");

        // Calculate due date from issue_date + due_days
        // Note: due_days can be 0 (due today), so check for null/undefined, not falsy
        if (
          invoiceData.issue_date &&
          record.due_days !== null &&
          record.due_days !== undefined
        ) {
          const dueDays = parseFloat(record.due_days) || 0;
          const dueDate = dayjs(invoiceData.issue_date).add(dueDays, "days");
          return dueDate.format("MMM DD, YYYY");
        }

        return "N/A";
      },
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 100,
      align: "right",
      render: (value) => `${parseFloat(value || 0).toFixed(2)}%`,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: (value, record) => {
        // If amount is provided, use it
        if (value && value > 0) {
          return `${invoiceData?.currency || "$"} ${parseFloat(value).toFixed(
            2
          )}`;
        }
        // Otherwise, calculate from percentage and total
        if (record.percentage && invoiceData?.total_amount) {
          const calculatedAmount =
            (parseFloat(invoiceData.total_amount) *
              parseFloat(record.percentage)) /
            100;
          return `${invoiceData?.currency || "$"} ${calculatedAmount.toFixed(
            2
          )}`;
        }
        return `${invoiceData?.currency || "$"} 0.00`;
      },
    },
  ];

  const totalDue = parseFloat(invoiceData?.total_amount || 0);
  const paidAmount = parseFloat(invoiceData?.paid_amount || 0);
  const balanceDue = totalDue - paidAmount;

  return (
    <Modal
      title={
        <Space>
          <Text strong style={{ fontSize: 18 }}>
            Simplified Tax {invoiceType === "bill" ? "Bill" : "Invoice"}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      centered
      zIndex={2100}
      footer={
        <Space>
          <Button icon={<CloseOutlined />} onClick={onCancel}>
            Close
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportPDF}
            loading={exportingPDF}
          >
            Export PDF
          </Button>
        </Space>
      }
      bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
    >
      <Spin spinning={loading}>
        <div className="invoice-template" style={{ padding: "24px" }}>
          {/* Header Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Invoice Number</Text>
                <div>
                  <Text strong style={{ fontSize: 18 }}>
                    {invoiceData?.number || `#${invoiceId}`}
                  </Text>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Date</Text>
                <div>
                  <Text strong>
                    {invoiceData?.issue_date
                      ? dayjs(invoiceData.issue_date).format("YYYY-MM-DD")
                      : "N/A"}
                  </Text>
                </div>
              </div>
              {invoiceData?.due_date && (
                <div>
                  <Text type="secondary">Due Date</Text>
                  <div>
                    <Text strong>
                      {dayjs(invoiceData.due_date).format("YYYY-MM-DD")}
                    </Text>
                  </div>
                </div>
              )}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              {/* Financial Summary - Compact Design */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div
                  style={{
                    padding: 10,
                    backgroundColor: "#f0f8ff",
                    borderRadius: 6,
                    border: "1px solid #1890ff",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Total Due
                  </Text>
                  <div>
                    <Text
                      strong
                      style={{
                        fontSize: 18,
                        color: "#1890ff",
                        display: "block",
                      }}
                    >
                      {invoiceData?.currency || "$"} {totalDue.toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Paid Amount Section */}
                <div
                  style={{
                    padding: 10,
                    backgroundColor: balanceDue <= 0.01 ? "#f0f9f0" : "#fff7e6",
                    borderRadius: 6,
                    border:
                      balanceDue <= 0.01
                        ? "1px solid #52c41a"
                        : "1px solid #faad14",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Paid Amount
                  </Text>
                  <div>
                    <Text
                      strong
                      style={{
                        fontSize: 18,
                        color: balanceDue <= 0.01 ? "#52c41a" : "#faad14",
                        display: "block",
                      }}
                    >
                      {invoiceData?.currency || "$"} {paidAmount.toFixed(2)}
                    </Text>
                  </div>
                </div>

                {/* Balance Due Section */}
                <div
                  style={{
                    padding: 10,
                    backgroundColor: balanceDue <= 0.01 ? "#f0f9f0" : "#fff1f0",
                    borderRadius: 6,
                    border:
                      balanceDue <= 0.01
                        ? "1px solid #52c41a"
                        : "1px solid #ff4d4f",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {balanceDue <= 0.01 ? "Status" : "Balance Due"}
                  </Text>
                  <div>
                    <Text
                      strong
                      style={{
                        fontSize: 18,
                        color: balanceDue <= 0.01 ? "#52c41a" : "#ff4d4f",
                        display: "block",
                      }}
                    >
                      {balanceDue <= 0.01 ? (
                        <span>Fully Paid ✓</span>
                      ) : (
                        <span>
                          {invoiceData?.currency || "$"} {balanceDue.toFixed(2)}
                        </span>
                      )}
                    </Text>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Divider />

          {/* Bill To Section */}
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              Bill To
            </Title>
            <div>
              <Text strong style={{ fontSize: 16 }}>
                {(() => {
                  // Try partner_name at root level first
                  if (invoiceData?.partner_name) {
                    return invoiceData.partner_name;
                  }
                  // Try partner object with name
                  if (invoiceData?.partner?.name) {
                    return invoiceData.partner.name;
                  }
                  // Try partner object with partner_name
                  if (invoiceData?.partner?.partner_name) {
                    return invoiceData.partner.partner_name;
                  }
                  // If partner is just an ID (number/string), don't show it
                  if (
                    invoiceData?.partner &&
                    typeof invoiceData.partner !== "object"
                  ) {
                    return "Unknown Partner";
                  }
                  // Fallback
                  return "Unknown Partner";
                })()}
              </Text>
            </div>
            {invoiceData?.partner?.address && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{invoiceData.partner.address}</Text>
              </div>
            )}
            {invoiceData?.partner?.phone && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{invoiceData.partner.phone}</Text>
              </div>
            )}
            {invoiceData?.partner?.email && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{invoiceData.partner.email}</Text>
              </div>
            )}
          </div>

          <Divider />

          {/* Items Table */}
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 12 }}>
              Item / Description
            </Title>
            <Table
              columns={itemColumns}
              dataSource={invoiceData?.items || []}
              pagination={false}
              size="small"
              rowKey="id"
              bordered
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4} align="right">
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                        {invoiceData?.currency || "$"} {totalDue.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>

          {/* Payment Terms Section */}
          {invoiceData?.payment_terms &&
            invoiceData.payment_terms.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Payment Terms</Title>
                <Table
                  columns={paymentTermColumns}
                  dataSource={invoiceData.payment_terms}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  bordered
                />
              </div>
            )}

          {/* Notes and Bank Details Section */}
          {(invoiceData?.notes ||
            invoiceData?.brand?.client?.bank_iban_usd ||
            invoiceData?.brand?.client?.bank_iban_aed ||
            invoiceData?.brand?.client?.bank_iban_eur) && (
            <div>
              <Title level={5}>Notes</Title>
              <div
                style={{
                  padding: 12,
                  backgroundColor: "#fafafa",
                  borderRadius: 4,
                  fontSize: 11,
                }}
              >
                {invoiceData?.notes && (
                  <div style={{ marginBottom: 16, whiteSpace: "pre-wrap" }}>
                    {invoiceData.notes}
                  </div>
                )}

                {/* Bank Details */}
                {invoiceData?.brand?.client?.bank_account_holder && (
                  <div style={{ marginTop: 12 }}>
                    <Text strong>
                      Account Holder:{" "}
                      {invoiceData.brand.client.bank_account_holder}
                    </Text>
                  </div>
                )}

                {invoiceData?.brand?.client?.bank_iban_usd && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>CURRENCY: USD</Text>
                    <div>
                      <Text>
                        IBAN: {invoiceData.brand.client.bank_iban_usd}
                      </Text>
                    </div>
                  </div>
                )}

                {invoiceData?.brand?.client?.bank_iban_aed && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>CURRENCY: AED</Text>
                    <div>
                      <Text>
                        IBAN: {invoiceData.brand.client.bank_iban_aed}
                      </Text>
                    </div>
                  </div>
                )}

                {invoiceData?.brand?.client?.bank_iban_eur && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>CURRENCY: EUR</Text>
                    <div>
                      <Text>
                        IBAN: {invoiceData.brand.client.bank_iban_eur}
                      </Text>
                    </div>
                  </div>
                )}

                {invoiceData?.brand?.client?.bank_swift && (
                  <div style={{ marginTop: 8 }}>
                    <Text>
                      BIC/SWIFT: {invoiceData.brand.client.bank_swift}
                    </Text>
                  </div>
                )}

                {invoiceData?.brand?.client?.bank_address && (
                  <div style={{ marginTop: 8 }}>
                    <Text>
                      Bank address: {invoiceData.brand.client.bank_address}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default InvoiceViewModal;
